import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, useMediaQuery } from "@mui/material";
import Button from "@/components/button";
import StorePicker from "../Components/StorePicker";
import DateRangePicker from "../Components/DateRangePicker";
import AddDeliveryModal from "@/modals/addDeliveryModal";
import AddCostModal from "@/modals/addCostModal";
import PreLoader from "@/components/preLoader";
import PurchaseCard from "./PurchaseCard";
import CustomizedSnackbars from "@/components/snackBar";
import PurchaseLoading from "./Loading";
import EmptyContainer from "../Components/EmptyContainer";
import TableComponent from "./Table";

import { useLazyQuery, useMutation } from "@apollo/client";
import {
  PURCHASE_STOCKS,
  UPDATE_PURCHASE_PRICE,
} from "@/services/storeService";

import {
  selectStores,
  selectStockTypes,
  selectLocalization,
} from "@/store/store/selectors";

import useStyles from "./styles";
import { isEmpty } from "lodash";
import moment from "moment";
import { Colors } from "@/theme";
import { MERCHANT_ID } from "@/constants/env";

const PurchaseStock = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const stores = useSelector(selectStores);
  const stockTypes = useSelector(selectStockTypes);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  const [store, setStore] = useState(null);
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days").startOf("day"),
    moment().endOf("day"),
  ]);

  const [openAddDelivery, setOpenAddDelivery] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [purchaseStocks, setPurchaseStocks] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [getPurchaseStocks] = useLazyQuery(PURCHASE_STOCKS, {
    onCompleted: (data) => {
      setPurchaseStocks(data.purchase_stocks);
      setHasLoaded(true);
    },
    onError: () => setHasLoaded(true),
    fetchPolicy: "no-cache",
  });

  const [updatePurchasePriceMutation, { loading }] = useMutation(
    UPDATE_PURCHASE_PRICE,
    {
      context: {
        headers: {
          merchantId: MERCHANT_ID,
          storeId: store?.id,
        },
      },
      onCompleted: (data) => {
        onStockUpdateSuccess(t("purchase_screen.cost_added"));
      },
    }
  );

  useEffect(() => {
    if (!isEmpty(stores)) {
      setStore(stores[0]);
    }
  }, [stores]);

  const refetchPurchaseStocks = () => {
    setHasLoaded(false);
    getPurchaseStocks({
      context: {
        headers: {
          merchantId: MERCHANT_ID,
          storeId: store.id,
        },
      },
      variables: {
        from: moment(dateRange[0]).toISOString().replace("Z", ""),
        to: moment(dateRange[1]).toISOString().replace("Z", ""),
        stock_types: stockTypes.map((item) => item.id),
        product_ids: store.products?.map((item) => item.id),
      },
    });
  };

  useEffect(() => {
    if (!!store?.id) {
      refetchPurchaseStocks();
    }
  }, [store?.id, dateRange]);

  const onStockUpdateSuccess = (msg) => {
    setSnackMessage(msg);
    refetchPurchaseStocks();
  };

  const onAddCost = (stock, price) => {
    updatePurchasePriceMutation({
      variables: {
        prices: [
          {
            stock_id: stock.id,
            purchase_price: price,
          },
        ],
      },
    });
  };

  return (
    <div>
      <Box
        display={"flex"}
        flexDirection={isDesktop ? "row" : "column"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box display={"flex"}>
          <span className={classes.regionalText}>
            {t("purchase_screen.purchase_stock")}
          </span>
          <Box ml={5}>
            <Button
              color="secondary"
              className={classes.addDeliveryButton}
              variant="text"
              onClick={() => setOpenAddDelivery(true)}
            >
              {t("purchase_screen.add_delivery")}
            </Button>
          </Box>
        </Box>
        <Box display={"flex"}>
          <DateRangePicker
            dateRange={dateRange}
            onChangeDateRange={setDateRange}
          />
          <Box ml={6}>
            <StorePicker
              stores={stores}
              store={store}
              onChange={(value) => {
                setStore(value);
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box mt={10}>
        {!!store && (
          <TableComponent
            products={store.products}
            loading={!hasLoaded}
            stockTypes={stockTypes}
            purchaseStocks={purchaseStocks?.sort(
              (a, b) => moment(b.update_time) - moment(a.update_time)
            )}
            onAddCost={onAddCost}
          />
        )}
      </Box>

      {openAddDelivery && (
        <AddDeliveryModal
          store={store}
          stockTypes={stockTypes}
          products={store?.products}
          open={openAddDelivery}
          onClose={() => setOpenAddDelivery(false)}
          onUpdateSuccess={() =>
            onStockUpdateSuccess(t("purchase_screen.stock_added"))
          }
        />
      )}

      <CustomizedSnackbars
        open={!!snackMessage}
        onClose={() => setSnackMessage("")}
        type="success"
        message={snackMessage}
      />

      {loading && <PreLoader size={40} />}
    </div>
  );
};

export default PurchaseStock;
